import type { AdminExhibitionPurpose } from '@repo/api-contracts';
import { type ReactElement, useState } from 'react';
import { movedOrder } from '@/shared/lib';
import { ExhibitionPurposeTab } from './exhibition-purpose-tab';

interface ExhibitionPurposeTabListProps {
  readonly purposes: readonly AdminExhibitionPurpose[];
  readonly selectedPurposeId: string | null;
  readonly isBusy: boolean;
  readonly onSelect: (purposeId: string) => void;
  readonly onRename: (purpose: AdminExhibitionPurpose) => void;
  readonly onDelete: (purpose: AdminExhibitionPurpose) => void;
  readonly onReorder: (orderedIds: readonly string[]) => void;
}

/** 목적 탭 줄 — 끌어다 놓으면 목적 전체 순서를 제출한다(드래그 인덱스는 이 목록이 갖는다). */
export const ExhibitionPurposeTabList = ({
  purposes,
  selectedPurposeId,
  isBusy,
  onSelect,
  onRename,
  onDelete,
  onReorder,
}: ExhibitionPurposeTabListProps): ReactElement => {
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);

  const handleDrop = (index: number): void => {
    // 요청 하나가 진행 중이면 화면의 버튼을 모두 잠근다(§8-6) — 드래그도 예외가 아니다.
    if (isBusy) {
      setDraggingIndex(null);
      return;
    }

    if (draggingIndex !== null && draggingIndex !== index) {
      onReorder(
        movedOrder(
          purposes.map((purpose) => purpose.id),
          draggingIndex,
          index,
        ),
      );
    }

    setDraggingIndex(null);
  };

  return (
    <ul className="flex flex-wrap items-center gap-2">
      {purposes.map((purpose, index) => (
        <ExhibitionPurposeTab
          key={purpose.id}
          purpose={purpose}
          isSelected={purpose.id === selectedPurposeId}
          isDragging={draggingIndex === index}
          isBusy={isBusy}
          onSelect={() => onSelect(purpose.id)}
          onDragStart={() => setDraggingIndex(index)}
          onDrop={() => handleDrop(index)}
          onDragEnd={() => setDraggingIndex(null)}
          onRename={() => onRename(purpose)}
          onDelete={() => onDelete(purpose)}
        />
      ))}
    </ul>
  );
};
